import type {
  BackupInit,
  BackupPullCreated,
  BackupPullInit,
  BackupPullStatus,
  BackupRestoreCreated,
  BackupRestoreInit,
  BackupRestoreStatus,
  BackupUploadResult,
  ExternalS3BackupConfig,
  ExternalS3BackupList,
  GlobalSettings,
  LocalBackupList,
  LocalBackupType,
  RecoveryConfig,
  RecoveryConfigUpdate,
} from 'common/api/v1/types'
import { EdgeClient } from 'common/generated/edgeClient'
import { tryConvertDates } from 'common/serialization'
import { ApplicationException } from '../ApplicationException'
import { fetch, handleUnauthorizedResponse, makeHttpApplicationException } from '../../utils/fetch'

const parseXhrBody = (xhr: XMLHttpRequest): unknown => {
  if (xhr.responseType === 'json') {
    return xhr.response !== null ? tryConvertDates(xhr.response) : undefined
  }

  if (!xhr.responseText) return undefined

  const contentType = xhr.getResponseHeader('content-type')
  if (!contentType?.includes('application/json')) return xhr.responseText

  try {
    return tryConvertDates(JSON.parse(xhr.responseText))
  } catch {
    return xhr.responseText
  }
}

export class SettingsApi {
  constructor(private readonly edgeClient: EdgeClient) {}

  getSettings(): Promise<GlobalSettings> {
    return this.edgeClient.getGlobalsettings()
  }
  saveSettings(params: GlobalSettings): Promise<GlobalSettings> {
    return this.edgeClient.setGlobalsettings(params)
  }

  async createBackupAsync(params: BackupInit) {
    await this.edgeClient.createBackup(params)
  }

  async testS3Write(params: ExternalS3BackupConfig) {
    await this.edgeClient.testBackupExternalS3s(params)
  }

  getRecoveryConfig(): Promise<RecoveryConfig> {
    return this.edgeClient.getRecovery()
  }

  saveRecoveryConfig(params: RecoveryConfigUpdate): Promise<RecoveryConfig> {
    return this.edgeClient.setRecovery(params)
  }

  listExternalS3Backups(): Promise<ExternalS3BackupList> {
    return this.edgeClient.getBackupExternalS3()
  }

  async listLocalBackups(type: LocalBackupType): Promise<LocalBackupList> {
    const result = await fetch<LocalBackupList>(`/api/backup/${type}`, {
      method: 'GET',
    })
    return result.body
  }

  async deleteLocalConfigBackup(filename: string): Promise<void> {
    await fetch<void>(`/api/backup/config/${encodeURIComponent(filename)}`, {
      method: 'DELETE',
    })
  }

  pullExternalS3Backup(params: BackupPullInit): Promise<BackupPullCreated> {
    return this.edgeClient.pullBackupExternalS3s(params)
  }

  getExternalS3PullStatus(): Promise<BackupPullStatus> {
    return this.edgeClient.getBackupExternalS3PullStatus()
  }

  restoreConfigBackup(params: BackupRestoreInit): Promise<BackupRestoreCreated> {
    return this.edgeClient.restoreBackup('config', params)
  }

  getRestoreStatus(): Promise<BackupRestoreStatus> {
    return this.edgeClient.getBackupRestoreStatus('config')
  }

  async uploadBackupFile(file: File, onProgress?: (percent: number) => void): Promise<BackupUploadResult> {
    return new Promise<BackupUploadResult>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', '/api/backup/config/upload')
      xhr.responseType = 'json'
      xhr.setRequestHeader('content-type', file.type || 'application/gzip')

      xhr.upload.onprogress = (event) => {
        if (!onProgress || !event.lengthComputable || event.total <= 0) return
        // Upload progress events track bytes sent, not server-side processing.
        // Keep max at 99 until the HTTP response confirms completion.
        const percent = Math.max(0, Math.min(99, Math.round((event.loaded * 100) / event.total)))
        onProgress(percent)
      }

      xhr.onerror = () => {
        reject(
          new ApplicationException({
            fatal: true,
            errorCode: 'network_error',
            text: 'Upload failed',
            details: 'Upload failed due to a network error',
            origin: undefined,
          }),
        )
      }

      xhr.onload = () => {
        const body = parseXhrBody(xhr)

        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(100)
          resolve(body as BackupUploadResult)
          return
        }

        handleUnauthorizedResponse(xhr.status, xhr.responseURL)
        reject(makeHttpApplicationException(xhr.status, xhr.statusText, body, undefined))
      }

      xhr.send(file)
    })
  }
}
